import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import '../../../css/pages.css';

// Motion component aliases
const MotionDiv = motion.div;
const MotionSpan = motion.span;
const MotionP = motion.p;

const BounceText = ({ text, className }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });
  const words = text.split(' ');

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const wordVariants = {
    hidden: { 
      opacity: 0,
      y: 20,
      scale: 0.8
    },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: i * 0.1
      }
    }),
    bounce: (i) => ({
      y: [0, -15 + Math.random() * 10, 0],
      x: [0, (Math.random() - 0.5) * 8, 0],
      scale: [1, 1.1, 1],
      transition: {
        duration: 0.8,
        ease: "easeInOut",
        delay: i * 0.05,
        repeat: 1
      }
    })
  };

  return (
    <MotionDiv 
      ref={ref}
      className={className}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      {words.map((word, index) => (
        <MotionSpan
          key={index}
          custom={index}
          variants={wordVariants}
          animate={isInView ? ["visible", "bounce"] : "hidden"}
          className="bounce-word"
          whileHover={{
            scale: 1.1,
            color: "#ff6b9d",
            transition: { duration: 0.2 }
          }}
        >
          {word}{index < words.length - 1 ? ' ' : ''}
        </MotionSpan>
      ))}
    </MotionDiv>
  );
};

const TestimonialCard = ({ review }) => (
  <div className={`testimonial-card ${review.text.length > 150 ? 'large' : review.text.length > 100 ? 'medium' : 'small'}`}>
    <div className="stars">{review.stars}</div>
    <p className="testimonial-text">"{review.text}"</p>
    <div className="reviewer">
      <div className="reviewer-avatar">{review.avatar}</div>
      <div className="reviewer-info">
        <h4>{review.name}</h4>
        <p>{review.business}</p>
      </div>
    </div>
  </div>
);

const VerticalScroll = ({ reviews, direction = "up", animationDuration = 60 }) => {
  // Duplicate reviews for seamless loop
  const duplicatedReviews = [...reviews, ...reviews, ...reviews];
  
  return (
    <div className="vertical-scroll-container">
      <div 
        className={`vertical-scroll-content scroll-${direction}`}
        style={{
          animationDuration: `${animationDuration}s`,
          animationDirection: direction === 'up' ? 'reverse' : 'normal'
        }}
      >
        {duplicatedReviews.map((review, index) => (
          <TestimonialCard 
            key={`${direction}-${index}`} 
            review={review}
          />
        ))}
      </div>
    </div>
  );
};

const Reviews = () => {

  const reviews = [
    {
      stars: "★★★★★",
      text: "POSlah! completely transformed my chicken rice stall. Sales increased by 40% in just 3 months, and the AI insights helped me optimize my menu perfectly.",
      name: "Lim Wei Ming",
      business: "Tian Tian Chicken Rice",
      avatar: "LW"
    },
    {
      stars: "★★★★★", 
      text: "The customer queue management feature is a game-changer. No more long waiting times, and customers love the smooth ordering experience.",
      name: "Muthu Raman",
      business: "Raja's Prata Palace", 
      avatar: "MR"
    },
    {
      stars: "★★★★★",
      text: "Best investment I ever made! The inventory tracking saved me from running out of ingredients during peak hours. Highly recommended!",
      name: "Sarah Lim",
      business: "Laksa Paradise",
      avatar: "SL"
    },
    {
      stars: "★★★★★",
      text: "Amazing customer service and the AI recommendations actually work! My daily revenue increased by 35% since switching to POSlah.",
      name: "Ahmad Rahman",
      business: "Nasi Lemak Corner",
      avatar: "AR"
    },
    {
      stars: "★★★★★",
      text: "The real-time analytics help me make better decisions every day. I can see exactly which dishes are trending and adjust accordingly.",
      name: "Chen Li Hua",
      business: "Wanton Noodle House",
      avatar: "CH"
    },
    {
      stars: "★★★★★",
      text: "Setup was incredibly easy and the team was so helpful. Within a week, we were processing orders 3x faster than before!",
      name: "Raj Patel",
      business: "Curry House Express",
      avatar: "RP"
    },
    {
      stars: "★★★★★",
      text: "Wah, this system really shiok lah! My char kway teow stall now very modern. Customers also happy because no need wait so long.",
      name: "Uncle Tan",
      business: "Ah Tan's Famous CKT",
      avatar: "UT"
    },
    {
      stars: "★★★★★",
      text: "AI recommendations damn good sia! Tell me which dish will sell well, which one need to improve. My profit up 50% already!",
      name: "Jenny Wong",
      business: "Jenny's Zi Char",
      avatar: "JW"
    },
    {
      stars: "★★★★★",
      text: "Super easy to use leh. My aunties also can learn in one day. No more manual counting, everything automated. Very the convenient!",
      name: "Mdm Siti",
      business: "Siti's Nasi Padang",
      avatar: "MS"
    },
    {
      stars: "★★★★★",
      text: "Steady lah this POS system! Queue management very smart, customers no need to squeeze and wait. My hawker center also recommend to other stalls.",
      name: "Ah Beng",
      business: "Beng's Wanton Mee",
      avatar: "AB"
    }
  ];

  // Split reviews for two columns
  const leftColumnReviews = reviews.slice(0, 5);
  const rightColumnReviews = reviews.slice(5);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 60, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="reviews-new" id="reviews">
      <MotionDiv 
        className="reviews-container-new"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="reviews-content-new">
          {/* Left Column */}
          <div className="reviews-left-column">
            <BounceText 
              text="What Clients Have To Say"
              className="reviews-main-title"
            />
            
            <MotionP 
              className="reviews-subtitle"
              variants={itemVariants}
            >
              Real feedback from real business owners who kena transformed their operations with POSlah lah! 
              Confirm plus chop these testimonials are genuine one.
            </MotionP>

            <MotionDiv variants={itemVariants}>
              <button 
                className="read-more-btn"
                onClick={() => {
                  // Future: Navigate to testimonials page
                  console.log('Navigate to testimonials page');
                }}
              >
                <span>Read More Stories</span>
                <i className="bi bi-arrow-right"></i>
              </button>
            </MotionDiv>
          </div>

          {/* Right Column - Dual Vertical Scroll */}
          <div className="reviews-right-column">
            <div className="scroll-columns-container">
              {/* First Column - Scrolling Up */}
              <div className="scroll-column">
                <VerticalScroll 
                  reviews={leftColumnReviews} 
                  direction="up" 
                  animationDuration={45}
                />
              </div>

              {/* Second Column - Scrolling Down */}
              <div className="scroll-column">
                <VerticalScroll 
                  reviews={rightColumnReviews} 
                  direction="down" 
                  animationDuration={50}
                />
              </div>
            </div>

            {/* Gradient Overlays */}
            <div className="scroll-gradient-top"></div>
            <div className="scroll-gradient-bottom"></div>
          </div>
        </div>
      </MotionDiv>
    </div>
  );
};

export default Reviews;